import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import useRequest from "@/lib/request/useRequest";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Select,
    SelectItem,
    SelectSection,
    type Selection,
} from "@heroui/react";
import SimpleLayer from "./SimpleLayer";
import {
    APPLICATION_OPERATION_IMAGE_LAYER,
    APPLICATION_OPERATIONS_SOURCE,
    HARVEST_OPERATION_IMAGE_LAYER,
    HARVEST_OPERATIONS_SOURCE,
    PLANTING_OPERATION_IMAGE_LAYER,
    PLANTING_OPERATIONS_SOURCE,
} from "@/lib/map/layers";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { ImageSource } from "mapbox-gl";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useI18n } from "@/lib/i18n/useI18n";

function OperationsLayers() {
    const { formatDateMMDDYYYY } = useI18n();
    const { fieldId, fieldBbox, cropZones } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const { map, mapLoaded } = useMap();

    const [selectedApplicationId, setSelectedApplicationId] =
        useState<Selection>(new Set());
    const [selectedHarvestId, setSelectedHarvestId] = useState<Selection>(
        new Set(),
    );
    const [selectedPlantingId, setSelectedPlantingId] = useState<Selection>(
        new Set(),
    );
    const applicationVisibleRef = useRef(false);
    const harvestVisibleRef = useRef(false);
    const plantingVisibleRef = useRef(false);

    const showCropZoneLabels = (cropZones?.length ?? 0) > 1;

    const cropZoneNameById = useMemo(() => {
        return new Map((cropZones ?? []).map((zone) => [zone.id, zone.name]));
    }, [cropZones]);

    const formatCropZoneLabel = (cropZoneId?: number | null) => {
        if (!showCropZoneLabels || cropZoneId == null) return "";
        const name = cropZoneNameById.get(cropZoneId);
        return name ? ` (${name})` : "";
    };

    const operations = useGetRequest<{
        operations: {
            id: number;
            average_N: number;
            start_date: string;
            bounds: number[];
            crop_zone?: number | null;
        }[];
    }>({
        url: `fields/${fieldId}/application_operations/`,
    });

    const harvestOperations = useGetRequest<
        {
            id: number;
            irrigated_averages: {
                yield: number;
                moisture: number;
                corrected_yield: null;
            };
            start_date: string;
            bounds: number[];
            crop_zone?: number | null;
        }[]
    >({
        url: `fields/${fieldId}/harvest_operations/`,
    });

    const plantingOperations = useGetRequest<
        {
            id: number;
            start_date: string;
            average_seeding_rate: number;
            average_planting_depth: number;
            bounds: number[];
            crop_zone?: number | null;
        }[]
    >({
        url: `fields/${fieldId}/planting_operations/`,
    });

    const imageQuery = useGetRequest({
        url: `application_operations/${[...selectedApplicationId][0]}/map_display_image/`,
        responseType: "blob",
        enabled: [...selectedApplicationId].length > 0,
    });

    const [harvestId, band] = useMemo(() => {
        const id = ([...selectedHarvestId][0] as string)?.split(".")[0] ?? null;
        const band =
            ([...selectedHarvestId][0] as string)?.split(".")[1] ?? null;
        return [id, band];
    }, [selectedHarvestId]);

    const harvestImageQuery = useGetRequest({
        url: `harvest_operations/${harvestId}/map_display_image/?band=${band}`,
        responseType: "blob",
        enabled: [...selectedHarvestId].length > 0,
    });

    const [plantingId, plantingBand] = useMemo(() => {
        const id =
            ([...selectedPlantingId][0] as string)?.split(".")[0] ?? null;
        const band =
            ([...selectedPlantingId][0] as string)?.split(".")[1] ?? null;
        return [id, band];
    }, [selectedPlantingId]);

    const plantingImageQuery = useGetRequest({
        url: `planting_operations/${plantingId}/map_display_image/?band=${plantingBand}`,
        responseType: "blob",
        enabled: [...selectedPlantingId].length > 0,
    });

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox =
            [...selectedApplicationId][0] !== undefined
                ? (invertedTurfBboxToFourCornerCoordinates(
                      operations.data?.operations.find(
                          (op) =>
                              op.id.toString() ===
                              [...selectedApplicationId][0],
                      )?.bounds,
                  ) as Coordinates)
                : undefined;

        if (imageUrl) {
            const imSource = map.getSource(
                APPLICATION_OPERATIONS_SOURCE,
            ) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(APPLICATION_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer({
                    ...APPLICATION_OPERATION_IMAGE_LAYER,
                    layout: {
                        ...APPLICATION_OPERATION_IMAGE_LAYER.layout,
                        visibility: applicationVisibleRef.current
                            ? "visible"
                            : "none",
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [APPLICATION_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [APPLICATION_OPERATIONS_SOURCE]);
        }
    }, [
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        operations.data,
        selectedApplicationId,
    ]);

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !harvestImageQuery.data
            ? null
            : window.URL.createObjectURL(harvestImageQuery.data as Blob);
        const bbox = harvestId
            ? (invertedTurfBboxToFourCornerCoordinates(
                  harvestOperations.data?.find(
                      (op) => op.id.toString() === harvestId,
                  )?.bounds,
              ) as Coordinates)
            : undefined;

        if (imageUrl) {
            const imSource = map.getSource(
                HARVEST_OPERATIONS_SOURCE,
            ) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(HARVEST_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer({
                    ...HARVEST_OPERATION_IMAGE_LAYER,
                    layout: {
                        ...HARVEST_OPERATION_IMAGE_LAYER.layout,
                        visibility: harvestVisibleRef.current
                            ? "visible"
                            : "none",
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [HARVEST_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [HARVEST_OPERATIONS_SOURCE]);
        }
    }, [
        harvestImageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        harvestId,
        harvestOperations.data,
    ]);

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !plantingImageQuery.data
            ? null
            : window.URL.createObjectURL(plantingImageQuery.data as Blob);
        const bbox = plantingId
            ? (invertedTurfBboxToFourCornerCoordinates(
                  plantingOperations.data?.find(
                      (op) => op.id.toString() === plantingId,
                  )?.bounds,
              ) as Coordinates)
            : undefined;

        if (imageUrl) {
            const imSource = map.getSource(
                PLANTING_OPERATIONS_SOURCE,
            ) as ImageSource;
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(PLANTING_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer({
                    ...PLANTING_OPERATION_IMAGE_LAYER,
                    layout: {
                        ...PLANTING_OPERATION_IMAGE_LAYER.layout,
                        visibility: plantingVisibleRef.current
                            ? "visible"
                            : "none",
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [PLANTING_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [PLANTING_OPERATIONS_SOURCE]);
        }
    }, [
        plantingImageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        plantingId,
        plantingOperations.data,
    ]);

    return (
        <>
            <SimpleLayer
                name="As-Applied"
                layerNames={[APPLICATION_OPERATION_IMAGE_LAYER.id]}
                onDownload={undefined}
                onValueChange={(visible) => {
                    applicationVisibleRef.current = visible;
                }}
            >
                {() => {
                    return (
                        <Select
                            aria-label="Select As-Applied"
                            className="mt-2"
                            selectedKeys={selectedApplicationId}
                            onSelectionChange={setSelectedApplicationId}
                            size="sm"
                        >
                            {sortArrayByKey(
                                operations.data?.operations ?? [],
                                "start_date",
                                "desc",
                            ).map((operation) => {
                                const textValue = `${formatDateMMDDYYYY(operation.start_date)}${formatCropZoneLabel(operation.crop_zone)}
                                    '-- Rate:
                                    ${roundTo(operation.average_N, 1)}
                                    lbs N`;
                                return (
                                    <SelectItem
                                        key={operation.id}
                                        textValue={textValue}
                                    >
                                        {textValue}
                                    </SelectItem>
                                );
                            })}
                        </Select>
                    );
                }}
            </SimpleLayer>
            <SimpleLayer
                name="As-Harvested"
                layerNames={[HARVEST_OPERATION_IMAGE_LAYER.id]}
                onDownload={undefined}
                onValueChange={(visible) => {
                    harvestVisibleRef.current = visible;
                }}
            >
                {() => {
                    const disabledKeys = (harvestOperations.data ?? [])
                        .map((operation) => {
                            const keys = [];
                            if (!operation.irrigated_averages?.yield) {
                                keys.push(`${operation.id}.1`);
                            }
                            if (!operation.irrigated_averages?.moisture) {
                                keys.push(`${operation.id}.2`);
                            }
                            if (
                                !operation.irrigated_averages?.corrected_yield
                            ) {
                                keys.push(`${operation.id}.3`);
                            }
                            return keys;
                        })
                        .flat();
                    return (
                        <Select
                            aria-label="Select As-Applied"
                            className="mt-2"
                            selectedKeys={selectedHarvestId}
                            onSelectionChange={setSelectedHarvestId}
                            disabledKeys={disabledKeys}
                            size="sm"
                        >
                            {sortArrayByKey(
                                harvestOperations.data ?? [],
                                "start_date",
                                "desc",
                            ).map((operation) => {
                                const items = [
                                    {
                                        key: `${operation.id}.1`,
                                        label: `Yield: ${roundTo(
                                            operation.irrigated_averages
                                                ?.yield ?? 0,
                                            0,
                                        )} bu/ac`,
                                        disabled:
                                            !operation.irrigated_averages
                                                ?.yield,
                                    },
                                    {
                                        key: `${operation.id}.2`,
                                        label: `Moisture: ${roundTo(
                                            operation.irrigated_averages
                                                ?.moisture ?? 0,
                                            0,
                                        )}%`,
                                        disabled:
                                            !operation.irrigated_averages
                                                ?.moisture,
                                    },
                                    {
                                        key: `${operation.id}.3`,
                                        label: `Corrected Yield: ${roundTo(
                                            operation.irrigated_averages
                                                ?.corrected_yield ?? 0,
                                            0,
                                        )} bu/ac`,
                                    },
                                ];
                                return (
                                    <SelectSection
                                        key={operation.id}
                                        showDivider
                                        title={`${formatDateMMDDYYYY(
                                            operation.start_date,
                                        )}${formatCropZoneLabel(operation.crop_zone)}`}
                                    >
                                        {items.map((item) => (
                                            <SelectItem
                                                key={item.key}
                                                textValue={item.label}
                                                isReadOnly={item.disabled}
                                            >
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectSection>
                                );
                            })}
                        </Select>
                    );
                }}
            </SimpleLayer>
            <SimpleLayer
                name="As-Planted"
                layerNames={[PLANTING_OPERATION_IMAGE_LAYER.id]}
                onDownload={undefined}
                onValueChange={(visible) => {
                    plantingVisibleRef.current = visible;
                }}
            >
                {() => {
                    const disabledKeys = (plantingOperations.data ?? [])
                        .map((operation) => {
                            const keys = [];
                            if (!operation.average_seeding_rate) {
                                keys.push(`${operation.id}.1`);
                            }
                            if (!operation.average_planting_depth) {
                                keys.push(`${operation.id}.2`);
                            }
                            return keys;
                        })
                        .flat();
                    return (
                        <Select
                            aria-label="Select As-Applied"
                            className="mt-2"
                            selectedKeys={selectedPlantingId}
                            onSelectionChange={setSelectedPlantingId}
                            disabledKeys={disabledKeys}
                            size="sm"
                        >
                            {sortArrayByKey(
                                plantingOperations.data ?? [],
                                "start_date",
                                "desc",
                            ).map((operation) => {
                                const items = [
                                    {
                                        key: `${operation.id}.1`,
                                        label: `Seeding Rate: ${roundTo(
                                            operation.average_seeding_rate ?? 0,
                                            0,
                                        )} sds/ac`,
                                        disabled:
                                            !operation.average_seeding_rate,
                                    },
                                    {
                                        key: `${operation.id}.2`,
                                        label: `Planting Depth: ${roundTo(
                                            operation.average_planting_depth ??
                                                0,
                                            0,
                                        )} in`,
                                        disabled:
                                            !operation.average_planting_depth,
                                    },
                                ];
                                return (
                                    <SelectSection
                                        key={operation.id}
                                        showDivider
                                        title={`${formatDateMMDDYYYY(
                                            operation.start_date,
                                        )}${formatCropZoneLabel(operation.crop_zone)}`}
                                    >
                                        {items.map((item) => (
                                            <SelectItem
                                                key={item.key}
                                                textValue={item.label}
                                                isReadOnly={item.disabled}
                                            >
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectSection>
                                );
                            })}
                        </Select>
                    );
                }}
            </SimpleLayer>
        </>
    );
}

export default OperationsLayers;
